import { ShieldCheck, Radar, Radio, Handshake } from "lucide-react";
import { getHead, PHONE, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../seo/routes";
import { Seo } from "../components/Seo";
import { PageHero, Prose, CTABand, SectionHead } from "../components/Blocks";

const head = getHead("/about");

const standards = [
  { icon: ShieldCheck, title: "Licensing First", text: "Every guard holds a valid provincial or territorial security licence and passes background screening before deployment." },
  { icon: Radar, title: "Verified Coverage", text: "GPS-stamped checkpoints, supervisor audits and time-stamped digital reports document every shift we work." },
  { icon: Radio, title: "Always Reachable", text: "A 24/7 dispatch line answered by coordinators — not voicemail — for clients and guards in the field." },
  { icon: Handshake, title: "Accountable Partnership", text: "Written post orders, named supervisors and one command structure across every province we serve." },
];

export default function About() {
  return (
    <>
      <Seo title={head.title} description={head.description} path="/about" />
      <PageHero
        overline="About Us"
        title="Maze Security Solutions — Canada Security Guards"
        lead="A Canadian security guard company built on a simple standard: licensed people, supervised shifts and proof of coverage on every engagement."
      />
      <Prose
        paragraphs={[
          "Maze Security Solutions — known across the country as Canada Security Guards — provides professional security guard services in all 13 Canadian provinces and territories. We serve commercial towers, construction sites, retail centres, hospitals, campuses, hotels, industrial yards and residential communities in more than 55 cities, from Toronto and Montreal to Vancouver, Calgary, Halifax and Whitehorse.",
          "Our model is deliberately simple. Every engagement starts with a site walk-through and written post orders. Every guard is provincially licensed and background-checked. Every shift is supervised and verified with GPS-stamped checkpoints and digital incident reporting. And every client reaches a live coordinator — day or night — through one dispatch line.",
          "We believe security is a promise that has to be proven daily. That is why our reporting is built for property managers, boards and insurers: time-stamped, photographic and audit-ready. When you contract Maze Security, you always know who was on your site, when, and what they did.",
        ]}
      />
      <section className="bg-[#131620] border-y border-white/10" data-testid="about-standards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHead overline="Operating Standard" title="How We Work" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {standards.map((s) => (
              <div key={s.title} className="border border-white/10 bg-[#0D0E12] p-6 rounded-sm" data-testid={`standard-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <s.icon className="h-8 w-8 text-[#FF5500] mb-4" />
                <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="about-contact">
        <SectionHead overline="Reach Dispatch" title="One Line, Day or Night" />
        <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
          <a href={PHONE_TEL} data-testid="about-phone-button" className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-semibold px-6 py-4 rounded-sm transition-colors duration-200">
            {PHONE}
          </a>
          <a href={EMAIL_MAILTO} data-testid="about-email-button" className="flex-1 inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-medium px-6 py-4 rounded-sm transition-colors duration-200">
            {EMAIL}
          </a>
        </div>
      </section>
      <CTABand title="Ready When You Are" sub="Tell dispatch about your property and your timeline — coverage starts with a conversation." />
    </>
  );
}
